import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import {
  GitCompare, Trophy, ExternalLink, AlertCircle, Loader2, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { compareAtlasVehicles } from "../lib/api";
import { Button } from "../components/ui/button";
import { PriceTag } from "../components/PriceTag";
import { AvailabilityBadge } from "../components/AvailabilityBadge";
import { eur, km } from "../lib/format";

const COLORS = ["#0A2540", "#2f6df6", "#00a37a", "#c026d3", "#ea580c"];
const METRIC_ORDER = ["priceValue", "mileageVsAge", "vehicleAge", "specification", "marketPosition", "dataConfidence"];

const SOURCE_LABEL = {
  autoscout24: "AutoScout24", mobile_de: "Mobile.de", "mobile-de": "Mobile.de", otomoto: "Otomoto",
  leboncoin: "Leboncoin", autotrader: "AutoTrader UK", "autotrader-uk": "AutoTrader UK",
};

const scoreCls = (s) =>
  s == null ? "text-slate-400"
    : s >= 70 ? "text-emerald-600"
    : s >= 50 ? "text-amber-600"
    : "text-rose-600";

export default function AtlasCompare() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputCount, setInputCount] = useState(0);

  useEffect(() => {
    let raw = [];
    try { raw = JSON.parse(sessionStorage.getItem("atlas_compare_set") || "[]"); } catch { raw = []; }
    setInputCount(raw.length);
    if (raw.length < 2) { setLoading(false); return; }
    compareAtlasVehicles(raw)
      .then(setProfile)
      .catch((e) => setError(e?.response?.data?.detail || "Comparison failed. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 text-center text-muted-foreground" data-testid="atlas-compare-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        <p className="mt-2 text-sm">Building your Atlas Decision Profile…</p>
      </div>
    );
  }

  if (inputCount < 2) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center" data-testid="atlas-compare-empty">
        <GitCompare className="h-8 w-8 text-muted-foreground mx-auto" />
        <p className="mt-3 font-medium">Select at least 2 vehicles to compare.</p>
        <p className="text-sm text-muted-foreground">Go to live search, tap “Compare” on a few listings, then compare them.</p>
        <Button className="mt-6 bg-atlas-ink hover:bg-atlas-blue text-white" onClick={() => navigate("/atlas")} data-testid="atlas-compare-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to search
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center" data-testid="atlas-compare-error">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto" />
        <p className="mt-3 font-medium">{error}</p>
        <Button className="mt-6" variant="outline" onClick={() => navigate("/atlas")}>Back to search</Button>
      </div>
    );
  }

  const vehicles = profile.vehicles || [];
  const ranked = [...vehicles].sort((a, b) => a.rank - b.rank);
  const labels = profile.metricLabels || {};
  const rec = profile.recommendation;
  const showRadar = vehicles.length <= 3;

  // Radar data: one row per metric axis, one key per vehicle (insufficient -> 0, flagged in table).
  const radarData = METRIC_ORDER.map((mk) => {
    const row = { metric: labels[mk] || mk };
    vehicles.forEach((v) => {
      const m = v.metrics[mk];
      row[v.label] = m && m.available ? m.score : 0;
    });
    return row;
  });

  const recColor = rec ? COLORS[vehicles.findIndex((v) => v.id === rec.id) % COLORS.length] : COLORS[0];

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="atlas-compare-page">
      <button onClick={() => navigate("/atlas")} className="inline-flex items-center text-sm text-muted-foreground hover:text-atlas-blue mb-4" data-testid="atlas-compare-back-link">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to search
      </button>
      <div className="flex items-center gap-2 text-atlas-blue mb-2">
        <GitCompare className="h-5 w-5" />
        <span className="overline">Atlas Decision Profile · {vehicles.length} vehicles</span>
      </div>
      <h1 className="font-display text-4xl font-bold tracking-tight">Compare & decide</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl text-sm">
        Every metric is a transparent calculation over the real listing data — not a valuation or an AI score.
        Where the data can’t support a metric, it reads “Insufficient data”. Assumption: expected mileage
        ≈ {profile.assumptions?.kmPerYear?.toLocaleString?.("en-US")} km/yr.
      </p>

      {/* Recommendation */}
      {rec && (
        <div className="mt-6 rounded-md border-2 p-5" style={{ borderColor: recColor }} data-testid="atlas-recommendation">
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: recColor }}>
            <Trophy className="h-4 w-4" /> ATLAS RECOMMENDS #1 · Confidence: <span data-testid="atlas-rec-confidence">{rec.confidence}</span>
          </div>
          <div className="mt-1 font-display text-2xl font-bold" data-testid="atlas-rec-label">{rec.label}</div>
          <div className="text-sm text-muted-foreground">Overall Atlas Decision score: <span className="font-mono">{rec.overallScore}</span> / 100</div>
          <ul className="mt-3 space-y-1 text-sm">
            {rec.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`atlas-rec-reason-${i}`}>
                <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Radar (2–3 vehicles) */}
      {showRadar && (
        <div className="mt-8 bg-card border border-border rounded-md p-4" data-testid="atlas-radar">
          <div className="text-sm font-semibold mb-2">Decision profile (higher is stronger)</div>
          <div style={{ width: "100%", height: 380, minHeight: 380 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={380}>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                {vehicles.map((v, i) => (
                  <Radar key={v.id} name={v.label} dataKey={v.label}
                    stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.18} />
                ))}
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Axes with no reliable data are plotted at 0 and marked “Insufficient data” in the table below.
          </p>
        </div>
      )}

      {/* Ranking table */}
      <div className="mt-8 overflow-x-auto" data-testid="atlas-ranking-table">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 pr-3 font-medium">Rank / Vehicle</th>
              <th className="py-2 px-3 font-medium text-right">Overall</th>
              {METRIC_ORDER.map((mk) => (
                <th key={mk} className="py-2 px-3 font-medium text-right whitespace-nowrap">{labels[mk] || mk}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ranked.map((v, idx) => (
              <tr key={v.id} className="border-b border-border/60 align-top" data-testid={`atlas-rank-row-${v.rank}`}>
                <td className="py-3 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-sm text-white text-xs font-bold" style={{ background: COLORS[vehicles.findIndex((x) => x.id === v.id) % COLORS.length] }}>{v.rank}</span>
                    <div>
                      <Link to={`/atlas/vehicle/${v.sourceCode}/${v.listingId}`} className="font-medium hover:text-atlas-blue">{v.label}</Link>
                      <div className="text-[11px] text-muted-foreground">{SOURCE_LABEL[v.sourceCode] || v.sourceCode}</div>
                    </div>
                  </div>
                </td>
                <td className={`py-3 px-3 text-right font-mono font-semibold ${scoreCls(v.overallScore)}`} data-testid={`atlas-overall-${v.rank}`}>
                  {v.overallScore != null ? v.overallScore : "—"}
                </td>
                {METRIC_ORDER.map((mk) => {
                  const m = v.metrics[mk];
                  return (
                    <td key={mk} className="py-3 px-3 text-right">
                      {m && m.available ? (
                        <span className={`font-mono ${scoreCls(m.score)}`}>{m.score}</span>
                      ) : (
                        <span className="text-[11px] text-slate-400 italic">Insufficient</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-vehicle evidence + factual attributes + provenance */}
      <div className="mt-10 grid gap-5 md:grid-cols-2">
        {ranked.map((v) => {
          const p = v.provenance || {};
          const color = COLORS[vehicles.findIndex((x) => x.id === v.id) % COLORS.length];
          const availState = p.availabilityState || "UNKNOWN";
          const availChecked = p.availabilityCheckedAt || p.lastSeenAt;
          const unavailable = availState === "UNAVAILABLE";
          const attr = (label, value, tid) => (
            <div className="flex justify-between gap-3 py-1.5 border-b border-border/50">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right font-medium" data-testid={tid}>
                {value === null || value === undefined || value === "" ? "Unavailable" : value}
              </span>
            </div>
          );
          return (
            <div key={v.id}
              className={`bg-card border rounded-md p-4 ${unavailable ? "border-red-200 opacity-80" : "border-border"}`}
              data-testid={`atlas-evidence-${v.rank}`}>
              <div className="flex items-center gap-3">
                {p.photos && p.photos.length ? (
                  <img src={p.photos[0]} alt={v.label} className="h-14 w-20 object-cover rounded-sm border border-border"
                    data-testid={`atlas-compare-thumb-${v.rank}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="h-14 w-20 flex items-center justify-center rounded-sm border border-border bg-muted text-[10px] text-muted-foreground text-center"
                    data-testid={`atlas-compare-noimg-${v.rank}`}>Image unavailable</div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-sm text-white text-[11px] font-bold" style={{ background: color }}>{v.rank}</span>
                    <div className="font-display text-base font-semibold truncate">{v.label}</div>
                  </div>
                  <div className="mt-1">
                    <AvailabilityBadge state={availState} checkedAt={availChecked} testid={`atlas-compare-availability-${v.rank}`} />
                  </div>
                </div>
              </div>

              {unavailable && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-xs px-3 py-2" data-testid={`atlas-compare-removed-${v.rank}`}>
                  Listing no longer available on {SOURCE_LABEL[p.sourceCode] || p.sourceCode}. Last known {availChecked ? new Date(availChecked).toLocaleDateString() : "—"}.
                </div>
              )}

              {/* Price (market currency primary, original always visible) */}
              <div className="mt-3">
                <PriceTag originalPrice={p.originalPrice} originalCurrency={p.originalCurrency} size="md" testid={`atlas-compare-price-${v.rank}`} />
              </div>

              {/* Factual attributes from the live source */}
              <div className="mt-3 text-xs" data-testid={`atlas-compare-attrs-${v.rank}`}>
                {attr("Make / model", `${p.brand || ""} ${p.model || ""}`.trim() || null)}
                {attr("Generation", p.generation)}
                {attr("Variant / trim", p.trim)}
                {attr("First registration", p.year)}
                {attr("Mileage", p.mileageKm != null ? km(p.mileageKm) : null)}
                {attr("Fuel", p.fuelType)}
                {attr("Transmission", p.transmission)}
                {attr("Body", p.bodyType)}
                {attr("Country", p.sourceCountry ? `${p.sourceCountry}${p.sourceCountryBasis === "source_default" ? " (source default)" : ""}` : null)}
                {attr("Marketplace", SOURCE_LABEL[p.sourceCode] || p.sourceCode)}
                {attr("Listing ID", p.listingId)}
                {attr("Normalized (EUR)", p.priceConverted && p.normalizedPriceEur != null ? eur(p.normalizedPriceEur) : (p.normalizedPriceEur != null ? eur(p.normalizedPriceEur) : "Unavailable"))}
                {attr("Last known status", `${availState}${availChecked ? " · " + new Date(availChecked).toLocaleDateString() : ""}`, `atlas-compare-lastknown-${v.rank}`)}
              </div>

              <div className="mt-3 text-sm">
                <div className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-1">Why</div>
                <ul className="space-y-1">
                  {v.whyReasons.map((r, i) => <li key={i} className="text-muted-foreground">• {r}</li>)}
                </ul>
              </div>

              {p.listingUrl ? (
                <a href={p.listingUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-sm text-atlas-blue hover:underline" data-testid={`atlas-listing-link-${v.rank}`}>
                  View original listing <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <div className="mt-3 text-xs text-muted-foreground italic" data-testid={`atlas-listing-nolink-${v.rank}`}>Original listing URL unavailable</div>
              )}
            </div>
          );
        })}
      </div>

      {profile.unavailableMetrics?.length > 0 && (
        <p className="mt-8 text-xs text-muted-foreground" data-testid="atlas-unavailable-note">
          Metrics currently unavailable for one or more vehicles (shown as “Insufficient data”): {profile.unavailableMetrics.join(", ")}.
          This reflects the real data returned by the sources — Atlas does not fabricate missing values.
        </p>
      )}
    </div>
  );
}
